import { FC, Fragment, useCallback, useEffect, useReducer, useState } from 'react';

import dayjs from 'dayjs';
import { ChartPie } from 'lucide-react';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { DatePicker } from '@mui/x-date-pickers';
import { PickerValue } from '@mui/x-date-pickers/internals';

import { options } from '../form';

import { initialValues, isAllFieldsFilled, UserGuideStatisticsForm } from './form';
import { UserGuideStatisticCard } from './userGuideStatisticCard/UserGuideStatisticCard';

import css from './UserGuideStatistics.module.css';

import { userGuideService } from 'v2/api/userGuideService';
import { Loader } from 'v2/components/loader/Loader';
import { Select } from 'v2/components/select/Select';
import { UserGuideType } from 'v2/constants';

export const UserGuideStatistics: FC = () => {
  const [form, setForm] = useState<UserGuideStatisticsForm>(initialValues);

  const [isOpen, setOpen] = useReducer((state) => !state, false);

  const { data, isMutating, trigger, reset } = useSWRMutation(
    'getGuidesStatistics',
    userGuideService.getGuidesStatistics
  );

  useEffect(() => {
    if (isAllFieldsFilled(form)) {
      trigger({
        guideType: form.guideType,
        startDate: form.startDate!.format(),
        endDate: form.endDate!.format(),
      });
    }
  }, [form]);

  const handleChangeType = useCallback((guideType: UserGuideType) => {
    setForm((prev) => ({ ...prev, guideType }));
  }, []);

  const handleChangeDate = (field: keyof UserGuideStatisticsForm) => (value: PickerValue) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleClose = () => {
    reset();
    setForm(initialValues);
    setOpen();
  };

  return (
    <Fragment>
      <Button onClick={setOpen} startIcon={<ChartPie width={18} />} variant="outlined" size="large">
        Statistics
      </Button>

      <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Guide Statistics Overview</DialogTitle>

        <DialogContent className={css.userGuideDialogContent}>
          <Select value={form.guideType} label="Guide Type" options={options} onChange={handleChangeType} />

          <div className={css.userGuideDialogDatesRange}>
            <DatePicker
              value={form.startDate}
              onChange={handleChangeDate('startDate')}
              label="Start Date"
              format="DD.MM.YYYY"
            />

            <DatePicker
              value={form.endDate}
              onChange={handleChangeDate('endDate')}
              maxDate={dayjs()}
              label="End Date"
              format="DD.MM.YYYY"
            />
          </div>

          {isMutating ? (
            <Loader />
          ) : (
            data && (
              <div className={css.userGuideDialogStatistics}>
                {data.map((s) => (
                  <UserGuideStatisticCard key={s.guideKey} statistic={s} />
                ))}
              </div>
            )
          )}
        </DialogContent>
      </Dialog>
    </Fragment>
  );
};
